/**
 * Host-level facts for the partner DEVELOPER SITE.
 *
 * The same SPA bundle is served on the product hosts (turn-connect.com,
 * dev.turn-connect.com) and on the developer hosts (developer.turn-connect.com,
 * developer.dev.turn-connect.com). Which site renders is decided purely from the
 * hostname, so the developer site needs no separate build or deployment — only
 * an ingress rule pointing the developer host at the same frontend Service.
 *
 * Everything here is a pure function of (hostname, search) so it is testable
 * without a browser.
 */

export type DeveloperEnvironment = 'development' | 'production';

/** Query flag that forces the developer site on any host (local dev). */
export const DEVELOPER_SITE_QUERY = 'site=developer';

const DEV_MAIN_HOST = 'dev.turn-connect.com';
const PROD_MAIN_HOST = 'turn-connect.com';
const DEV_DEVELOPER_HOST = 'developer.dev.turn-connect.com';
const PROD_DEVELOPER_HOST = 'developer.turn-connect.com';

const norm = (hostname: string) => (hostname || '').trim().toLowerCase().replace(/\.$/, '');

/** True when the hostname is a developer host (developer.*). */
export function isDeveloperHostname(hostname: string): boolean {
  return norm(hostname).startsWith('developer.');
}

/** True when the query string carries the local-dev override `?site=developer`. */
export function hasDeveloperSiteQuery(search: string): boolean {
  try {
    return new URLSearchParams(search || '').get('site') === 'developer';
  } catch {
    return false;
  }
}

/** Should this page load render the developer site instead of the product app? */
export function isDeveloperSite(hostname: string, search: string): boolean {
  return isDeveloperHostname(hostname) || hasDeveloperSiteQuery(search);
}

/**
 * Production only for the real production hosts; everything else (dev hosts,
 * localhost, preview hosts) is development. Failing towards "development"
 * means an unknown host never claims to be production.
 */
export function developerEnvironment(hostname: string): DeveloperEnvironment {
  const h = norm(hostname);
  if (h === PROD_DEVELOPER_HOST || h === PROD_MAIN_HOST || h === `www.${PROD_MAIN_HOST}`) return 'production';
  return 'development';
}

/** The main product site matching this environment. */
export function mainSiteUrl(hostname: string): string {
  return developerEnvironment(hostname) === 'production' ? `https://${PROD_MAIN_HOST}/` : `https://${DEV_MAIN_HOST}/`;
}

/** The developer site matching this environment (used by the main-host banner). */
export function developerSiteUrl(hostname: string): string {
  return developerEnvironment(hostname) === 'production' ? `https://${PROD_DEVELOPER_HOST}/` : `https://${DEV_DEVELOPER_HOST}/`;
}

/**
 * The runnable demo partner ("Acme Recon Hub") for this environment. The demo
 * is served under /partner-demo/ on the MAIN host of each environment.
 */
export function demoUrl(hostname: string): string {
  return `${mainSiteUrl(hostname)}partner-demo/`;
}
